import { useMutation } from '@graphcommerce/graphql'
import { ConfigurableProduct, PROOFING_OPTION, ProductInterface } from '@graphcommerce/graphql-mesh'
import {
  ApolloCartErrorAlert,
  useClearCurrentCartId,
  useFormGqlMutationCart,
} from '@graphcommerce/magento-cart'
import { CartItemFragment } from '@graphcommerce/magento-cart-items'
import { ProductCustomizableFragment } from '@graphcommerce/magento-product/components/ProductCustomizable/ProductCustomizable.gql'
import { ConfigurableOptionsInputProps } from '@graphcommerce/magento-product-configurable/ConfigurableOptions/ConfigurableOptions'
import {
  ConfigurableProductAddToCartDocument,
  ConfigurableProductAddToCartMutationVariables,
} from '@graphcommerce/magento-product-configurable/graphql/ConfigurableProductAddToCart.gql'
import { Money } from '@graphcommerce/magento-store'
import { extendableComponent } from '@graphcommerce/next-ui'
import { i18n } from '@lingui/core'
import { Alert, Box, MenuItem, SwipeableDrawer, SxProps, Theme } from '@mui/material'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { AttachArtworkDocument } from '../Artwork/AttachArtwork.gql'
import { PrimaryButton, SecondaryButton } from '../Button'
import { fbqAddToCart } from '../FacebookPixel/events/fbqAddToCart'
import { FileUploadModal, ProofOptionEnum, s3File } from '../FileUpload/FileUploadModal'
import { gtagAddToCart } from '../GoogleAnalytics/events/gtagAddToCart'
import { klaviyoAddToCart } from '../Klaviyo/events/klaviyoAddToCart'
import { AddedToCart } from '../Minicart/AddedToCart'
import { globalContext } from '../NextUi/globalContext'
import { PaypalPayLater } from '../PaymentBraintree/PaypalPayLater/PaypalPayLater'
import { AttachProjectDataDocument } from '../PitchPrint/AttachProjectData.gql'
import { PitchPrint } from '../PitchPrint/PitchPrint'
import { ProductCustomizable } from '../Product/ProductCustomizable/ProductCustomizable'
import { ErrorPrompt } from '../Prompt/ErrorPrompt'
import { useConfigurableContext } from './ConfigurableContext/ConfigurableContext'
import { ConfigurableOptionsInput } from './ConfigurableOptions/ConfigurableOptions'

type ConfigurableProductAddToCartProps = {
  variables: Omit<ConfigurableProductAddToCartMutationVariables, 'cartId' | 'selectedOptions'>
  product: Pick<
    ProductInterface,
    'name' | 'sku' | 'requires_artwork' | 'categories' | 'uid' | 'design_id'
  >
  name: string
  optionEndLabels?: Record<string, React.ReactNode>
  children?: React.ReactNode
  additionalButtons?: React.ReactNode
  sx?: SxProps<Theme>
  optionsProps?: Omit<
    ConfigurableOptionsInputProps,
    'name' | 'sku' | 'control' | 'rules' | 'errors' | 'optionEndLabels'
  >
  callback?: () => void
}

const compName = 'ConfigurableOptionsInput' as const
const parts = ['form', 'button', 'finalPrice', 'quantity', 'divider', 'buttonWrapper'] as const
const { classes } = extendableComponent(compName, parts)

export function ConfigurableProductAddToCart(props: ConfigurableProductAddToCartProps) {
  const {
    name,
    children,
    variables,
    optionEndLabels,
    optionsProps,
    additionalButtons,
    product,
    sx = [],
    callback,
    ...buttonProps
  } = props

  const [attachArtwork] = useMutation(AttachArtworkDocument)
  const [attachProjectData] = useMutation(AttachProjectDataDocument)
  const [artworkFiles, setArtworkFiles] = useState<Array<s3File>>([])
  const [ppProjectId, setPpProjectId] = useState<null | string>(null)
  const [isDisplayFileUploadModal, setIsDisplayFileUploadModal] = useState<boolean>(false)
  const { drawer, setDrawer } = useContext(globalContext)
  const [addedToCartDrawer, setAddedToCartDrawer] = useState<boolean | null>(null)
  const [shouldRetry, setShouldRetry] = useState(false)

  const [isDisplayErrorPrompt, setIsDisplayErrorPrompt] = useState<boolean>(false)
  const [attachError, setAttachError] = useState<Error>()
  const clearCurrentCartId = useClearCurrentCartId()
  const { getVariants, selection } = useConfigurableContext(variables.sku)
  const selectedVariant = getVariants(selection)
  const selectedVariantDesignId = selectedVariant?.at(0)?.product?.design_id
  const requiresArtwork = !selectedVariantDesignId && product.requires_artwork
  const [setupPriceDisplay, setSetupPriceDisplay] = useState('0')
  const [totalPriceDisplay, setTotalPriceDisplay] = useState('0')
  const [artworkComment, setArtworkComment] = useState('')
  const [proofOption, setProofOption] = useState<ProofOptionEnum>(ProofOptionEnum.wait_for_proof)

  const handleUploadArtwork = () => {
    setIsDisplayFileUploadModal(true)
  }

  const form = useFormGqlMutationCart(ConfigurableProductAddToCartDocument, {
    defaultValues: { ...variables },
    onBeforeSubmit: ({ selectedOptions, ...vars }) => {
      if ((requiresArtwork && !artworkFiles.length) || (selectedVariantDesignId && !ppProjectId)) {
        return false
      }

      return {
        ...vars,
        selectedOptions: Object.values(selectedOptions),
      }
    },
    onComplete: ({ data, errors }, vars) => {
      // When we have an invalid cartId (maybe they have been previously signIn as a customer, now the customer token is expired, but cartId is still there)
      if (
        errors?.find((error) => error.message.indexOf('user cannot perform operations on cart') > 0)
      ) {
        clearCurrentCartId()
        setShouldRetry(true)
        return
      }

      if (data?.addProductsToCart?.user_errors.length) {
        return
      }

      const addedItem = data?.addProductsToCart?.cart.items?.slice(-1)[0]

      gtagAddToCart(addedItem as unknown as CartItemFragment, 1, { added_from: 'product_page' })

      const category = product.categories?.length
        ? product.categories?.at(product.categories.length - 1)?.name
        : ''

      fbqAddToCart(addedItem as unknown as CartItemFragment, 1, category || '')
      klaviyoAddToCart(addedItem as unknown as CartItemFragment, 1, category || '')

      const cartItemUid = data?.addProductsToCart?.cart.items?.slice(-1)[0]?.uid ?? ''

      if (data && artworkFiles && artworkFiles.length > 0) {
        artworkFiles.forEach(async (artworkFile) => {
          await attachArtwork({
            variables: {
              cartItemUid,
              fileName: artworkFile.fileName ?? '',
              fileUrl: artworkFile.url ?? '',
              proofingOption:
                (proofOption.toString() as PROOFING_OPTION) ?? ('' as PROOFING_OPTION),
              comments: artworkComment ?? '',
            },
          })
            .catch((error: Error) => {
              setAttachError(error)
              setIsDisplayErrorPrompt(true)
            })
            .finally(() => setArtworkFiles([]))
        })
      }
      if (data && ppProjectId) {
        attachProjectData({
          variables: {
            cartItemUid,
            cartUid: data.addProductsToCart?.cart.id as string,
            projectId: ppProjectId,
          },
        })
          .then(() => {
            setPpProjectId(null)
          })
          .catch((error: Error) => {})
      }
    },
  })

  const { handleSubmit, formState, required, control, error, data, setValue, watch } = form
  const submitHandler = handleSubmit(() => {})

  useEffect(() => {
    if (shouldRetry) {
      setShouldRetry(false)
      submitHandler()
        .then(() => {})
        .catch(() => {})
    }
  }, [shouldRetry, submitHandler])

  useEffect(() => {
    if (selectedVariant.at(0)?.product?.__typename === 'SimpleProduct') {
      const tiers = selectedVariant.at(0)?.product?.price_tiers ?? []
      const currentQty = form.getValues('quantity')
      const currentAvailable = tiers.some((t) => String(t?.quantity) === String(currentQty))
      if (!currentAvailable) {
        const firstQty = tiers[0]?.quantity
        if (firstQty) setValue('quantity', firstQty)
      }
    }
  }, [selectedVariant, setValue])

  useEffect(() => {
    if (formState.isSubmitting) {
      setAddedToCartDrawer(null)
      setDrawer({ id: null })
    }
  }, [formState.isSubmitting, setDrawer, setAddedToCartDrawer])

  useEffect(() => {
    if (
      !formState.isSubmitting &&
      formState.isSubmitSuccessful &&
      !error?.message &&
      !data?.addProductsToCart?.user_errors?.length &&
      drawer.id === null &&
      addedToCartDrawer === null &&
      (!product.requires_artwork || artworkFiles.length || ppProjectId)
    ) {
      setDrawer({ id: 'added_to_cart' })
      setAddedToCartDrawer(true)
    }
  }, [
    data,
    error,
    formState,
    drawer,
    setDrawer,
    addedToCartDrawer,
    setAddedToCartDrawer,
    product,
    artworkFiles,
    ppProjectId,
  ])

  const quantityOptionLabelRenderer = useCallback((option, label, index) => {
    if (index === 0) {
      return label
    }

    if (typeof label === 'number') {
      return <Money value={label} formatOptions={{ maximumFractionDigits: index === 1 ? 4 : 2 }} />
    }
    const labelParts = label.split('#')
    return (
      <>
        <span className='line-through'>${labelParts[0]}</span> <br />{' '}
        <span className='text-brick'>${labelParts[1] || ''}</span>
      </>
    )
  }, [])

  const getProductPlusSetupPrice = useCallback(
    (quantity: number, unitPrice: number) => {
      const selectedProduct = selectedVariant.at(0)?.product
      const rowPrice = unitPrice * quantity
      let setupPrice = selectedProduct?.setup_price ?? 0
      if (selectedProduct) {
        const { setup_price_discount, setup_price_quantity_threshold } = selectedProduct
        if (setup_price_quantity_threshold && quantity > setup_price_quantity_threshold) {
          setupPrice = 0
        } else if (setup_price_discount) {
          return { rowPrice, setupPrice: setup_price_discount }
        }
      }

      return { rowPrice, setupPrice }
    },
    [selectedVariant],
  )

  const breakpointOptions =
    selectedVariant.at(0)?.product?.price_tiers?.map((tier) => {
      const val = tier?.final_price?.value ?? 0
      const unitPrice = val.toFixed(val >= 1 ? 2 : 3)

      return {
        value: tier?.quantity || '',
        labels: [
          tier?.quantity?.toLocaleString() || '',
          (unitPrice || 0) as unknown as number,
          // tier && typeof tier?.quantity === 'number' && typeof unitPrice === 'number'
          //   ? getProductPlusSetupPrice(tier?.quantity, unitPrice)
          //   : '',
        ],
      }
    }) || []

  const breakupPriceMatrix = useMemo(
    () =>
      selectedVariant.at(0)?.product?.price_tiers?.map((tier) => {
        const unitPrice = tier?.final_price?.value ?? 0
        const decimals = unitPrice >= 1 ? 2 : 3
        const roundedUnit = parseFloat(unitPrice.toFixed(decimals))

        return {
          value: tier?.quantity,
          ...getProductPlusSetupPrice(tier?.quantity ?? 0, roundedUnit),
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedVariant],
  )

  const breakpointMenuItems = breakpointOptions.map((option) => (
    <MenuItem
      value={option.value}
      className='flex justify-between pr-5'
      sx={{ justifyContent: 'space-between' }}
    >
      {option.labels?.map((item, index) =>
        index === 0 ? <span>{item}</span> : <span>{`$${item} each`}</span>,
      )}
    </MenuItem>
  ))

  useEffect(() => {
    const priceMatrix = breakupPriceMatrix?.find(
      (item) => item.value?.toString() === form.getValues()?.quantity?.toString(),
    )
    setSetupPriceDisplay(priceMatrix?.setupPrice.toString() ?? '0')
    setTotalPriceDisplay(
      ((priceMatrix?.rowPrice ?? 0) + (priceMatrix?.setupPrice ?? 0)).toString() ?? '0',
    )
  }, [breakupPriceMatrix, form])

  return (
    <>
      <Box
        component='form'
        onSubmit={submitHandler}
        noValidate
        className={classes.form}
        sx={[
          {
            width: '100%',
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <div style={{ display: 'none' }}>
          <ConfigurableOptionsInput
            name='selectedOptions'
            sku={variables.sku}
            control={control}
            rules={{ required: required.selectedOptions }}
            errors={formState.errors}
            optionEndLabels={optionEndLabels}
            product={product}
            setValue={setValue}
            {...optionsProps}
          />
        </div>

        <ProductCustomizable
          product={product as ConfigurableProduct & ProductCustomizableFragment}
          form={form}
          selectedVariantSku={selectedVariant.at(0)?.product?.sku}
        />

        {selectedVariant.at(0) && !!selectedVariant.at(0)?.product?.price_tiers?.length && (
          <div className='mt-3.75'>
            <p className='mb-3 text-[14px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'>
              Pricing
            </p>
            <div className='rounded-[14px] bg-[rgba(120,120,128,0.08)]'>
              {selectedVariant.at(0)?.product?.price_tiers?.map((tier, i) => {
                const qty = tier?.quantity ?? 0
                const unitPrice = tier?.final_price?.value ?? 0
                const decimals = unitPrice >= 1 ? 2 : 3
                const unitPriceStr = unitPrice.toLocaleString('en-US', {
                  minimumFractionDigits: decimals,
                  maximumFractionDigits: decimals,
                })
                const roundedUnit = parseFloat(unitPrice.toFixed(decimals))
                const subtotalStr = (qty * roundedUnit).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
                const isSelected = String(watch('quantity') ?? '') === String(qty)
                return (
                  <button
                    key={i}
                    type='button'
                    onClick={() => {
                      setValue('quantity', qty)
                      const priceMatrix = breakupPriceMatrix?.find(
                        (item) => item.value?.toString() === qty.toString(),
                      )
                      setSetupPriceDisplay(priceMatrix?.setupPrice.toString() ?? '0')
                      setTotalPriceDisplay(
                        (
                          (priceMatrix?.rowPrice ?? 0) + (priceMatrix?.setupPrice ?? 0)
                        ).toString() ?? '0',
                      )
                    }}
                    className={`flex w-full items-center rounded-[4px] px-4 py-3.5 transition-all active:scale-[0.99] ${
                      isSelected
                        ? 'bg-[#D1CBCB33] shadow-[0px_2px_3px_0px_rgba(0,0,0,0.25)] ring-[1.6px] ring-inset ring-[#5D54DE]'
                        : ''
                    }`}
                  >
                    <span
                      className={`flex-1 text-left text-[15px] text-[#1C1C1E] ${isSelected ? 'font-bold' : 'font-medium'}`}
                    >
                      {qty.toLocaleString()}
                    </span>
                    <span
                      className={`flex-1 text-center text-[15px] text-[#1C1C1E] ${isSelected ? 'font-bold' : ''}`}
                    >
                      ${unitPriceStr} ea
                    </span>
                    <span
                      className={`flex-1 text-right text-[15px] text-[#1C1C1E] ${isSelected ? 'font-bold' : ''}`}
                    >
                      ${subtotalStr}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        )}
        <div className='mt-3.75 flex flex-col gap-3 rounded-34xl bg-black/3 px-5 py-3'>
          {setupPriceDisplay !== '0' && (
            <div className='Type-Large-Regular flex items-center justify-between'>
              <span>Set up</span>
              <span>
                <Money
                  value={parseFloat(setupPriceDisplay)}
                  currency={
                    selectedVariant?.at(0)?.product?.price_range?.minimum_price?.regular_price
                      ?.currency
                  }
                />
              </span>
            </div>
          )}
          <div className='Type-XL-Medium flex items-center justify-between'>
            <span>Total</span>
            <span>
              <Money
                value={parseFloat(totalPriceDisplay)}
                currency={
                  selectedVariant?.at(0)?.product?.price_range?.minimum_price?.regular_price
                    ?.currency
                }
              />
            </span>
          </div>
        </div>
        {children}

        {!!totalPriceDisplay && (
          <PaypalPayLater totalPrice={parseFloat(totalPriceDisplay) * 1} placement='product' />
        )}

        {requiresArtwork && (
          <div className='mb-5 mt-10'>
            {artworkFiles.length <= 0 && (
              <PrimaryButton className='w-full ' onClick={handleUploadArtwork} {...buttonProps}>
                {i18n._('Upload Artwork')}
              </PrimaryButton>
            )}
            {artworkFiles.length > 0 && (
              <SecondaryButton
                className='w-full '
                color='primary'
                onClick={handleUploadArtwork}
                {...buttonProps}
              >
                {i18n._('View Artwork')}
              </SecondaryButton>
            )}
          </div>
        )}
        <div>
          <PrimaryButton
            type='submit'
            // loading={`for`mState.isSubmitting}
            className={`add-to-cart w-full ${!requiresArtwork ? 'mt-9' : ''}`}
            disabled={
              (requiresArtwork && !artworkFiles.length) ||
              (!!selectedVariantDesignId && !ppProjectId)
            }
            loading={formState.isSubmitting}
            {...buttonProps}
          >
            {i18n._(/* i18n */ 'Add to Cart')}
          </PrimaryButton>

          {additionalButtons}
        </div>

        <ApolloCartErrorAlert error={error} />

        {data?.addProductsToCart?.user_errors.map((e) => (
          <Box key={e?.code}>
            <Alert severity='error'>{e?.message}</Alert>
          </Box>
        ))}

        <FileUploadModal
          modalId='fileUploadModal'
          isDiplayed={isDisplayFileUploadModal}
          setIsDisplayed={setIsDisplayFileUploadModal}
          setUploadedFile={setArtworkFiles}
          files={artworkFiles}
          artworkComment={artworkComment}
          setArtworkComment={setArtworkComment}
          setProofOption={setProofOption}
          proofOption={proofOption}
          callback={callback}
        />
        {selectedVariantDesignId && (
          <PitchPrint
            designId={selectedVariantDesignId}
            projectId={ppProjectId}
            productUid={product.uid}
            setPpProjectId={setPpProjectId}
          />
        )}
        <ErrorPrompt error={attachError ?? error} />
      </Box>
      <SwipeableDrawer
        anchor='right'
        onClose={() => {
          setDrawer({ id: false })
          setAddedToCartDrawer(false)
        }}
        onOpen={() => {}}
        open={drawer.id === 'added_to_cart'}
        classes={{
          paper: 'rounded-l-[10px] max-w-[520px]',
        }}
      >
        <AddedToCart
          sku={variables.sku}
          onClose={() => {
            setDrawer({ id: false })
            setAddedToCartDrawer(false)
          }}
        />
      </SwipeableDrawer>
    </>
  )
}
